import { useState, useEffect } from 'react';
import { X, FileText, Upload, BookOpen, Info, Search, Plus, Layers, FileEdit } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { documentApi, userApi, draftApi } from '@/services/api';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';

interface DocumentOnboardingProps {
  onClose: () => void;
  contextData?: any;
  sessionApplications?: any[];
  onDocumentAdded?: () => void;
  onOpenUploadModal?: () => void;
  onSwitchToBatch?: () => void;
}

const RESOURCE_TYPES = [
  { id: 'patent', label: 'US Patent', icon: FileText, description: 'Public US Patent Number' },
  { id: 'publication', label: 'US Publication', icon: FileText, description: 'Public US Publication Number' },
  { id: 'application', label: 'US Application', icon: FileText, description: 'Public US Application Number' },
  { id: 'foreign_patent', label: 'Foreign Patent', icon: FileText, description: 'Foreign Patent Number' },
  { id: 'draft', label: 'Draft Document', icon: FileEdit, description: 'Add a draft to active documents' },
  { id: 'upload', label: 'Upload Document', icon: Upload, description: 'Upload your own document' },
  { id: 'mpep', label: 'MPEP', icon: BookOpen, description: 'Manual of Patent Examining Procedure' },
  { id: 'cfr', label: '37 CFR', icon: Info, description: 'Code of Federal Regulations' },
  { id: 'usc', label: '35 USC', icon: Info, description: 'United States Code' },
];

export function DocumentOnboarding({
  onClose,
  contextData,
  sessionApplications = [],
  onDocumentAdded,
  onOpenUploadModal,
  onSwitchToBatch,
}: DocumentOnboardingProps) {
  const [selectedType, setSelectedType] = useState<string>('');
  const [documentIds, setDocumentIds] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [userDocuments, setUserDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();
  const [showActiveDocsInfoModal, setShowActiveDocsInfoModal] = useState(false);
  const [usageData, setUsageData] = useState<any>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);

  // Fetch user documents when patent, publication, or draft is selected
  useEffect(() => {
    const fetchUserDocuments = async () => {
      if (selectedType === 'patent' || selectedType === 'publication') {
        setLoadingDocuments(true);
        try {
          const documents = await documentApi.fetchSessionDocuments(100);
          // Filter by the selected type using file_type field
          const filtered = documents.filter(doc =>
            (doc.file_type || '').toLowerCase() === selectedType.toLowerCase()
          );
          setUserDocuments(filtered);
        } catch (error) {
          console.error('Failed to load user documents:', error);
          setUserDocuments([]);
        } finally {
          setLoadingDocuments(false);
        }
      } else if (selectedType === 'draft') {
        setLoadingDocuments(true);
        try {
          // Load drafts from API
          const response = await draftApi.listDrafts();
          const allDrafts = response.drafts || [];

          // Convert drafts to document format
          const draftDocs = allDrafts.map((draft: any) => ({
            id: draft.draft_id,
            resource_id: draft.draft_id,
            title: typeof draft.title === 'string' ? draft.title : (draft.title?.title || 'Untitled Draft'),
            file_type: 'draft',
            draft_type: draft.draft_type
          }));

          setUserDocuments(draftDocs);
        } catch (error) {
          console.error('Failed to load drafts:', error);
          setUserDocuments([]);
        } finally {
          setLoadingDocuments(false);
        }
      } else {
        setUserDocuments([]);
      }
    };

    fetchUserDocuments();
  }, [selectedType]);

  // Listen for job completion events
  // Note: Notifications are handled globally in Main.tsx, this only handles cleanup
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[DocumentOnboarding] Job completion event received:', event.detail);

      // Format type name properly (handle foreign_patent -> Foreign Patent)
      const resourceTypeName = docType === 'foreign_patent'
        ? 'Foreign Patent'
        : docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        removeLoadingDocument(docId, resourceTypeName);

        if (onDocumentAdded) {
          onDocumentAdded();
        }
      } else {
        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'patent') {
          context.current_patents = (context.current_patents || []).filter((id: string) => id !== docId);
        } else if (docType === 'publication') {
          context.current_publications = (context.current_publications || []).filter((id: string) => id !== docId);
        } else if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        } else if (docType === 'foreign_patent') {
          context.current_foreign_patents = (context.current_foreign_patents || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [onDocumentAdded]);

  // Fetch usage data on mount to show remaining quota
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const response = await userApi.fetchUsage();
        if (response) {
          setUsageData(response);
        }
      } catch (error) {
        console.error('Failed to fetch usage data:', error);
      } finally {
        setLoadingUsage(false);
      }
    };

    fetchUsageData();
  }, []);

  const validateNumbers = (input: string, resourceType: string) => {
    if (!input.trim()) {
      setValidationErrors([]);
      return;
    }

    const entries = input
      .split(/[\s\n]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    const errors: string[] = [];

    entries.forEach((entry, index) => {
      if (resourceType === 'foreign_patent') {
        // Foreign patent format: CC#######K (e.g., EP1234567A1, WO2024123456A1)
        const pattern = /^[A-Z]{2,3}\d{5,10}[A-Z]\d*$/i;
        if (!pattern.test(entry.replace(/\s/g, ''))) {
          errors.push(
            `Entry ${index + 1} "${entry}" does not match foreign patent format (e.g., EP1234567A1, WO2024123456A1)`
          );
        }
      } else {
        // Clean the entry for US patents/publications/applications
        let cleaned = entry
          .replace(/,/g, '')
          .replace(/\//g, '')
          .replace(/^US/i, '')
          .replace(/[A-Z]\d*$/i, '');

        if (resourceType === 'publication') {
          const pattern = /^2\d{3}0?\d{6}$/;
          if (!pattern.test(cleaned)) {
            errors.push(
              `Entry ${index + 1} "${entry}" does not match publication format (2DDDNNNNNN)`
            );
          }
        } else if (resourceType === 'patent') {
          const pattern = /^\d{7,8}$/;
          if (!pattern.test(cleaned)) {
            errors.push(
              `Entry ${index + 1} "${entry}" does not match patent format (7 or 8 digits)`
            );
          }
        } else if (resourceType === 'application') {
          const pattern = /^\d{8}$/;
          if (!pattern.test(cleaned)) {
            errors.push(
              `Entry ${index + 1} "${entry}" does not match application format (8 digits)`
            );
            }
        }
      }
    });

    setValidationErrors(errors);
  };

  const handleSubmit = async (overrideDocumentId?: string) => {
    if (!selectedType) {
      addNotification({
        type: 'error',
        title: 'No Resource Type Selected',
        message: 'Please select a resource type first',
      });
      return;
    }

    // Use override if provided, otherwise use state
    const idsToProcess = overrideDocumentId || documentIds;

    // Ensure idsToProcess is a string
    if (typeof idsToProcess !== 'string') {
      console.error('Invalid idsToProcess:', idsToProcess);
      return;
    }

    const entries = idsToProcess
      .split(/[\s\n]+/)
      .map((id) => id.trim())
      .filter(Boolean);

    if (entries.length === 0) {
      addNotification({
        type: 'error',
        title: 'No Documents Entered',
        message: `Please enter at least one ${selectedType} number`,
      });
      return;
    }

    // Only check validation errors if not using override (manual entry)
    if (!overrideDocumentId && validationErrors.length > 0) {
      addNotification({
        type: 'error',
        title: 'Validation Errors',
        message: 'Please fix the validation errors before submitting',
      });
      return;
    }

    // Check daily quota for patent/publication/application/foreign_patent types
    if (['patent', 'publication', 'application', 'foreign_patent'].includes(selectedType) && usageData?.daily_onboarding) {
      const remaining = usageData.daily_onboarding.patent_publications.remaining;
      if (remaining <= 0) {
        addNotification({
          type: 'error',
          title: 'Daily Limit Reached',
          message: `You have reached your daily limit for patent/publication onboarding (${usageData.daily_onboarding.patent_publications.limit} per day). The limit will reset tomorrow.`,
          duration: 8000,
        });
        return;
      }
      if (entries.length > remaining) {
        addNotification({
          type: 'warning',
          title: 'Approaching Daily Limit',
          message: `You are trying to add ${entries.length} documents but only have ${remaining} remaining today. Please reduce the number of documents or try again tomorrow.`,
          duration: 8000,
        });
        return;
      }
    }

    // Clean and process entries
    const cleanedIds = entries.map((id) => {
      let cleaned = id.replace(/,/g, '').replace(/\//g, '').replace(/\s/g, '');

      if (selectedType === 'foreign_patent') {
        // Foreign patents: keep the full format (e.g., EP1234567A1)
        // Just remove spaces and common separators
        cleaned = cleaned.toUpperCase();
      } else if (['publication', 'patent', 'application'].includes(selectedType)) {
        cleaned = cleaned.replace(/^US/i, '').replace(/[A-Z]\d*$/i, '');

        // For publications, ensure format is 2DDD0NNNNNN
        if (
          selectedType === 'publication' &&
          cleaned.length === 10 &&
          /^2\d{3}\d{6}$/.test(cleaned)
        ) {
          cleaned = cleaned.slice(0, 4) + '0' + cleaned.slice(4);
        }
      }

      return cleaned;
    });

    setIsSubmitting(true);

    // Add documents to session context immediately
    const stored = localStorage.getItem('session_context');
    const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

    // Track if there was a quota error (to prevent auto-closing modal)
    let quotaErrorOccurred = false;

    // Handle different resource types
    if (['patent', 'publication', 'application', 'foreign_patent'].includes(selectedType)) {
      // For patent/publication/application/foreign_patent, call fetchAndLoadDocument for each
      for (const cleanedId of cleanedIds) {
        try {
          const response = await documentApi.fetchAndLoadDocument(selectedType, cleanedId);

          // Check if response is an error (backend returns errors with 200 status)
          const responseData = Array.isArray(response) ? response[0] : response;

          if (responseData?.error === 'daily_limit_exceeded') {
            quotaErrorOccurred = true;
            const errorData = responseData;

            // Show notification with longer duration and add to notification center
            addNotification({
              type: 'error',
              title: 'Daily Limit Reached',
              message: `${errorData.message} You have used ${errorData.current}/${errorData.limit} of your daily allowance. The limit will reset tomorrow.`,
              duration: 20000,
              persistent: true,
            });

            // Refresh usage data
            try {
              const usageResponse = await userApi.fetchUsage();
              if (usageResponse) {
                setUsageData(usageResponse);
              }
            } catch (e) {
              console.error('Failed to refresh usage data:', e);
            }

            // Close the onboarding window
            setIsSubmitting(false);
            onClose();
            return;
          }

          if (response?.job_id) {
            // Add to session context
            if (selectedType === 'patent') {
              context.current_patents = [...(context.current_patents || []), cleanedId];
            } else if (selectedType === 'publication') {
              context.current_publications = [...(context.current_publications || []), cleanedId];
            } else if (selectedType === 'application') {
              context.current_applications = [...(context.current_applications || []), cleanedId];
            } else if (selectedType === 'foreign_patent') {
              context.current_foreign_patents = [...(context.current_foreign_patents || []), cleanedId];
            }

            // Update context immediately so it shows in UI
            localStorage.setItem('session_context', JSON.stringify(context));
            window.dispatchEvent(new Event('storage-update'));

            // Show processing notification
            // Format type name properly (handle foreign_patent -> Foreign Patent)
            const resourceTypeName = selectedType === 'foreign_patent'
              ? 'Foreign Patent'
              : selectedType.charAt(0).toUpperCase() + selectedType.slice(1);
            addNotification({
              type: 'info',
              title: `Loading ${resourceTypeName}`,
              message: `Processing ${cleanedId}...`,
              duration: 60000, // Long duration since we'll update it
            });

            // Add to loading documents tracking
            addLoadingDocument(cleanedId, resourceTypeName, cleanedId);

            // Start polling for job status
            pollJobStatus(response.job_id, selectedType, cleanedId);
          }
        } catch (error: any) {
          console.error(`Failed to submit ${selectedType} ${cleanedId}:`, error);
          console.log('Error response:', error?.response);
          console.log('Error response data:', error?.response?.data);

          // Extract error data - Flask returns [data, status_code] as array
          const responseData = Array.isArray(error?.response?.data)
            ? error.response.data[0]
            : error?.response?.data;

          console.log('Extracted error data:', responseData);

          // Check if it's a quota exceeded error
          if (responseData?.error === 'daily_limit_exceeded') {
            console.log('QUOTA EXCEEDED ERROR DETECTED');
            quotaErrorOccurred = true; // Set flag to prevent modal from closing
            const errorData = responseData;

            // Show notification
            addNotification({
              type: 'error',
              title: 'Daily Limit Reached',
              message: `${errorData.message} You have used ${errorData.current}/${errorData.limit} of your daily allowance.`,
              duration: 8000,
            });

            console.log('Notification shown, refreshing usage data...');

            // Refresh usage data
            try {
              const response = await userApi.fetchUsage();
              if (response) {
                setUsageData(response);
                console.log('Usage data refreshed:', response);
              }
            } catch (e) {
              console.error('Failed to refresh usage data:', e);
            }

            setIsSubmitting(false); // Re-enable submit button
            console.log('Returning early to keep modal open');
            return; // Exit early, don't close modal
          } else {
            console.log('Other error, showing generic notification');
            addNotification({
              type: 'error',
              title: 'Submission Failed',
              message: `Failed to submit ${selectedType} ${cleanedId}. Please try again.`,
              duration: 5000,
            });
            setIsSubmitting(false);
            return; // Also return on other errors to keep modal open
          }
        }
      }
    } else {
      // For other types (mpep, cfr, usc, etc.), add directly to context
      if (selectedType === 'mpep') {
        context.mpep_sections = [...(context.mpep_sections || []), ...cleanedIds];
      } else if (selectedType === 'cfr') {
        context.cfr_sections = [...(context.cfr_sections || []), ...cleanedIds];
      } else if (selectedType === 'usc') {
        context.usc_sections = [...(context.usc_sections || []), ...cleanedIds];
      }

      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Documents Added',
        message: `Added ${cleanedIds.length} ${selectedType} section(s) to session.`,
        duration: 5000,
      });
    }

    setIsSubmitting(false);

    if (onDocumentAdded) {
      onDocumentAdded();
    }

    // Reset and close
    setDocumentIds('');
    setSelectedType('');
    setValidationErrors([]);
    onClose();
  };

  const pollJobStatus = (jobId: string, docType: string, docId: string) => {
    console.log('[DocumentOnboarding] Starting poll via global store:', { jobId, docType, docId });
    startPolling(jobId, docType, docId);
  };

  const handleAddDraft = async (draftId: string) => {
    try {
      // Get the full draft from API
      const response = await draftApi.listDrafts();
      const allDrafts = response.drafts || [];
      const draftData = allDrafts.find((d: any) => d.draft_id === draftId);

      if (!draftData) {
        addNotification({
          type: 'error',
          title: 'Draft Not Found',
          message: 'Could not find the selected draft',
        });
        return;
      }

      // Normalize the draft data
      const draft = {
        draft_id: draftData.draft_id,
        title: typeof draftData.title === 'string' ? draftData.title : (draftData.title?.title || 'Untitled Draft'),
        content: typeof draftData.content === 'string' ? draftData.content : (draftData.content?.content || ''),
        draft_type: draftData.draft_type
      };

      // Add to active_drafts
      const activeDraftsStr = localStorage.getItem('active_drafts');
      const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];

      // Check if already active
      if (activeDrafts.some((d: any) => d.draft_id === draftId)) {
        addNotification({
          type: 'info',
          title: 'Already Active',
          message: 'This draft is already in active documents',
        });
        return;
      }

      // Add draft
      activeDrafts.push(draft);
      localStorage.setItem('active_drafts', JSON.stringify(activeDrafts));

      // Also update session context
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
      context.current_drafts = activeDrafts;
      localStorage.setItem('session_context', JSON.stringify(context));

      // Dispatch event to update UI
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Draft Added',
        message: `"${draft.title || 'Untitled Draft'}" added to active documents`,
      });

      if (onDocumentAdded) {
        onDocumentAdded();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to add draft:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to add draft to active documents',
      });
    }
  };

  // Calculate current resources
  const activeDraftsStr = typeof window !== 'undefined' ? localStorage.getItem('active_drafts') : null;
  const activeDraftsCount = activeDraftsStr ? JSON.parse(activeDraftsStr).length : 0;

  const resourceCounts = {
    patents: contextData?.current_patents?.length || 0,
    publications: contextData?.current_publications?.length || 0,
    applications: contextData?.current_applications?.length || 0,
    foreign_patents: contextData?.current_foreign_patents?.length || 0,
    drafts: activeDraftsCount,
    uploads: contextData?.resource_ids?.length || 0,
    mpep: contextData?.mpep_sections?.length || 0,
    cfr: contextData?.cfr_sections?.length || 0,
    usc: contextData?.usc_sections?.length || 0,
  };

  const totalCount = Object.values(resourceCounts).reduce((a, b) => a + b, 0);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900">
                Add Documents to Session
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowActiveDocsInfoModal(true)}
                className="h-7 w-7 p-0 text-sm font-semibold text-gray-600 border-gray-300 hover:bg-gray-100"
                title="How Active Documents work"
              >
                ?
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Select a document type and enter one or more document numbers
            </p>
            {!loadingUsage && usageData?.daily_onboarding && ['patent', 'publication', 'application', 'foreign_patent'].includes(selectedType) && (
              <div className="mt-2 flex items-center gap-2">
                <Badge
                  variant={usageData.daily_onboarding.patent_publications.remaining <= 0 ? 'destructive' : usageData.daily_onboarding.patent_publications.remaining <= 5 ? 'secondary' : 'default'}
                  className="text-xs"
                >
                  {usageData.daily_onboarding.patent_publications.remaining} / {usageData.daily_onboarding.patent_publications.limit} remaining today
                </Badge>
                {usageData.daily_onboarding.patent_publications.remaining <= 5 && usageData.daily_onboarding.patent_publications.remaining > 0 && (
                  <span className="text-xs text-amber-600">Approaching daily limit</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onSwitchToBatch && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSwitchToBatch}
                className="gap-2"
              >
                <Layers className="w-4 h-4" />
                Batch Mode
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Current Resources Summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-900 mb-2">
                Current Session Resources ({totalCount} total)
              </div>
              {totalCount > 0 ? (
                <div className="grid grid-cols-3 gap-2 text-xs text-gray-700">
                  {resourceCounts.patents > 0 && (
                    <div>Patents: {resourceCounts.patents}</div>
                  )}
                  {resourceCounts.publications > 0 && (
                    <div>Publications: {resourceCounts.publications}</div>
                  )}
                  {resourceCounts.applications > 0 && (
                    <div>Applications: {resourceCounts.applications}</div>
                  )}
                  {resourceCounts.foreign_patents > 0 && (
                    <div>Foreign Patents: {resourceCounts.foreign_patents}</div>
                  )}
                  {resourceCounts.drafts > 0 && (
                    <div>Drafts: {resourceCounts.drafts}</div>
                  )}
                  {resourceCounts.uploads > 0 && (
                    <div>Uploads: {resourceCounts.uploads}</div>
                  )}
                  {resourceCounts.mpep > 0 && <div>MPEP: {resourceCounts.mpep}</div>}
                  {resourceCounts.cfr > 0 && <div>CFR: {resourceCounts.cfr}</div>}
                  {resourceCounts.usc > 0 && <div>USC: {resourceCounts.usc}</div>}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  No resources in current session
                </div>
              )}
            </div>


            {/* Document List or Manual Entry */}
            {selectedType && selectedType !== 'upload' && (
              <div>
                {/* Show document list for patent/publication/draft, or if there are no documents, show manual entry */}
                {(selectedType === 'patent' || selectedType === 'publication' || selectedType === 'draft') && !showManualEntry ? (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-900">
                        Select from Your {selectedType === 'patent' ? 'Patents' : selectedType === 'publication' ? 'Publications' : 'Drafts'}
                      </label>
                      {selectedType !== 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowManualEntry(true)}
                          className="gap-2"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Enter Manually
                        </Button>
                      )}
                    </div>

                    {/* Search */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder={`Search your ${selectedType === 'draft' ? 'drafts' : selectedType + 's'}...`}
                        value={documentSearchQuery}
                        onChange={(e) => setDocumentSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>

                    {/* Document List */}
                    {loadingDocuments ? (
                      <div className="text-center py-8 text-gray-500">
                        Loading your documents...
                      </div>
                    ) : userDocuments.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-600 mb-3">
                          No {selectedType === 'draft' ? 'drafts' : selectedType + 's'} found in your library.
                        </p>
                        {selectedType !== 'draft' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowManualEntry(true)}
                          >
                            Enter Document Number Manually
                          </Button>
                        )}
                      </div>
                    ) : (
                      <ScrollArea className="h-64 border border-gray-200 rounded-lg">
                        <div className="p-2 space-y-2">
                          {userDocuments
                            .filter(doc => {
                              if (!documentSearchQuery) return true;
                              const searchLower = documentSearchQuery.toLowerCase();
                              const number = doc.resource_id?.toLowerCase() || '';
                              const title = doc.title?.toLowerCase() || '';
                              return number.includes(searchLower) || title.includes(searchLower);
                            })
                            .map((doc) => {
                              const docNumber = doc.resource_id?.replace(/^(patent-|publication-)/, '') || '';

                              // Check if already active
                              let isAlreadyActive = false;
                              if (selectedType === 'patent') {
                                isAlreadyActive = contextData?.current_patents?.includes(docNumber);
                              } else if (selectedType === 'publication') {
                                isAlreadyActive = contextData?.current_publications?.includes(docNumber);
                              } else if (selectedType === 'draft') {
                                const activeDraftsStr = localStorage.getItem('active_drafts');
                                const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
                                isAlreadyActive = activeDrafts.some((d: any) => d.draft_id === doc.resource_id);
                              }

                              return (
                                <div
                                  key={doc.id}
                                  className={`p-3 rounded-lg border transition-all ${
                                    isAlreadyActive
                                      ? 'bg-gray-100 border-gray-300 opacity-60'
                                      : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                                  }`}
                                  onClick={() => {
                                    if (!isAlreadyActive) {
                                      if (selectedType === 'draft') {
                                        // Add draft to active documents
                                        handleAddDraft(doc.resource_id);
                                      } else if (docNumber && docNumber.trim()) {
                                        handleSubmit(docNumber);
                                      }
                                    }
                                  }}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-gray-900">
                                        {selectedType === 'draft' ? doc.title : docNumber}
                                      </div>
                                      {selectedType !== 'draft' && doc.title && (
                                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                          {doc.title}
                                        </div>
                                      )}
                                    </div>
                                    {isAlreadyActive && (
                                      <Badge variant="secondary" className="text-xs ml-2">
                                        Active
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </ScrollArea>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-900">
                        Enter Document Numbers
                      </label>
                      {(selectedType === 'patent' || selectedType === 'publication') && showManualEntry && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowManualEntry(false);
                            setDocumentIds('');
                            setValidationErrors([]);
                          }}
                        >
                          ← Back to List
                        </Button>
                      )}
                    </div>
                    <Textarea
                      rows={4}
                      value={documentIds}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setDocumentIds(newValue);
                        if (['publication', 'patent', 'application', 'foreign_patent'].includes(selectedType)) {
                          validateNumbers(newValue, selectedType);
                        }
                      }}
                      className="resize-none"
                      placeholder={
                        selectedType === 'publication'
                          ? '20240123456\nUS20240123456A1\n2024/0123456'
                          : selectedType === 'patent'
                          ? '12345678\nUS12,345,678\n12/345,678'
                          : selectedType === 'application'
                          ? '12345678\n12/345,678'
                          : selectedType === 'foreign_patent'
                          ? 'EP1234567A1\nWO2024123456A1\nCN112345678A'
                          : '1234567\n7654321'
                      }
                    />

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="font-medium text-sm text-red-900 mb-2">
                          Validation Errors:
                        </div>
                        <ul className="list-disc list-inside space-y-1 text-xs text-red-700">
                          {validationErrors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Format Help */}
                    <div className="mt-2 text-xs text-gray-600">
                      {selectedType === 'publication' &&
                        'Expected format: 2DDDNNNNNN (e.g., 20240123456)'}
                      {selectedType === 'patent' &&
                        'Expected format: 7 or 8 digits (e.g., 9424461, 12345678)'}
                      {selectedType === 'application' &&
                        'Expected format: 8 digits (e.g., 12345678)'}
                      {selectedType === 'foreign_patent' &&
                        'Expected format: CC#######K (e.g., EP1234567A1, WO2024123456A1, CN112345678A)'}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Resource Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Select Document Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                {RESOURCE_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => {
                        if (type.id === 'upload') {
                          if (onOpenUploadModal) {
                            onOpenUploadModal();
                          }
                        } else {
                          setSelectedType(type.id);
                          setDocumentIds('');
                          setValidationErrors([]);
                        }
                      }}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                        selectedType === type.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          selectedType === type.id
                            ? 'text-blue-600'
                            : 'text-gray-400'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-medium text-sm ${
                            selectedType === type.id
                              ? 'text-blue-900'
                              : 'text-gray-900'
                          }`}
                        >
                          {type.label}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {type.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => handleSubmit()} disabled={!selectedType || isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Documents'}
          </Button>
        </div>
        </div>
      </div>

      {showActiveDocsInfoModal && (
        <div
          className="fixed inset-0 z-[150] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowActiveDocsInfoModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6 space-y-4 text-sm text-gray-600 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3"
              onClick={() => setShowActiveDocsInfoModal(false)}
            >
              <X className="w-4 h-4" />
            </Button>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Active Documents & Usage</h3>
              <p className="text-gray-600 mt-1">
                Anything you add here becomes part of the Active Document list for this session. Chat, tasks, and exports will all
                pull from these materials.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Why they matter</h4>
              <p>
                When you add a patent, application, publication, or upload, it becomes immediately available for citations and
                grounding. Keep only the docs you need so responses stay focused.
              </p>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">Batch mode for multiple documents</h4>
              <p className="mb-2">
                Need to add many documents at once? Click the "Batch Mode" button to switch to batch onboarding.
              </p>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li>Enter multiple document numbers (comma or line separated)</li>
                <li>Processes 3 documents at a time automatically</li>
                <li>Shows real-time progress and status for each document</li>
                <li>Perfect for onboarding large portfolios or prior art sets</li>
              </ul>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">Daily document allotment</h4>
              <p>
                Each new document you pull in—manual entry or upload—counts toward your daily allowance. Check your remaining usage
                from the profile menu or by clicking the credits indicator in the top-right corner.
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Tip: remove documents you no longer need to stay within limits and keep your workspace tidy.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
